import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import NavBar from '../../components/common/NavBar';
import Icon from '../../components/common/Icon';
import './Legal.css';

const LAST_UPDATED = '2026-08-30';

const Terms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Screen>
      <div className="top">
        <div className="legal">
          <div className="legal__header">
            <button
              type="button"
              className="legal__back"
              onClick={() => navigate(-1)}
              aria-label={t('back', { defaultValue: 'Back' })}
            >
              <Icon name="angle-left" />
            </button>
            <h1 className="legal__title">{t('termsOfService', { defaultValue: 'Terms of Service' })}</h1>
          </div>

          <section className="legal__hero">
            <div className="legal__hero-kicker">Tornea</div>
            <h2 className="legal__hero-title">
              {t('termsOfService', { defaultValue: 'Terms of Service' })}
            </h2>
            <div className="legal__hero-meta">
              {t('lastUpdated', { defaultValue: 'Last updated' })}: {LAST_UPDATED}
            </div>
          </section>

          <article className="legal__body">
            <p>
              Welcome to Tornea. These Terms of Service ("Terms") govern your use of the Tornea app, website, and
              event platform (the "Service"). The Service is operated by <strong>Herculean CV</strong>, VAT
              BE 0830.631.784, Liersesteenweg 240A/7, 2220 Heist-op-den-Berg, Belgium, for events and communities in
              the European Economic Area, and by <strong>Hercules Trophy Middle East FZ LLC</strong>, licence
              2705/2012, Creative Tower, Hamad Bin Abdullah, P.O. Box 4422, Fujairah, United Arab Emirates, for
              events and communities everywhere else (together "Tornea", "we", "us"). Your agreement is with the
              company responsible for the event or community you take part in. By creating an account or otherwise
              using the Service, you agree to these Terms.
            </p>

            <h2>1. Eligibility</h2>
            <p>
              You must be at least 13 years old (or the minimum digital-consent age in your country) to use the
              Service. If you use the Service on behalf of an organization or company, you represent that you have
              the authority to bind that organization to these Terms.
            </p>

            <h2>2. Your account</h2>
            <ul>
              <li>You are responsible for everything that happens under your account.</li>
              <li>Keep your password secret. Notify us promptly of any unauthorized use at <a href="mailto:info@tornea.net">info@tornea.net</a>.</li>
              <li>Provide accurate information and keep it up to date.</li>
            </ul>

            <h2>3. User content</h2>
            <p>
              You retain ownership of content you submit (photos, comments, team info, social posts, etc.). By
              submitting content, you grant Tornea a worldwide, non-exclusive, royalty-free license to host,
              display, reproduce, and distribute that content as needed to operate the Service — including showing
              it on public team and company pages and within events you participate in.
            </p>
            <p>
              You are solely responsible for the content you submit. You confirm that you have the necessary rights
              to share it and that it does not violate any third-party rights or applicable laws.
            </p>
            <p>
              Content can be removed under section 5. Removing it does not erase the record of why it was removed:
              reports and moderation decisions are kept as described in our{' '}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>

            <h2>4. Acceptable use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Post content that is unlawful, harassing, hateful, defamatory, deceptive, or sexually explicit.</li>
              <li>Impersonate someone else or misrepresent your affiliation with a person or organization.</li>
              <li>Cheat, manipulate scores, votes, or leaderboards, or interfere with normal event flow.</li>
              <li>Reverse-engineer, scrape at scale, or attempt to break the security of the Service.</li>
              <li>Use the Service to send spam or unsolicited messages.</li>
            </ul>

            <h2>5. Reporting, moderation and enforcement</h2>
            <p>
              Anywhere the Service lets you post — event walls, company feeds, photo albums — it also lets you
              report something you believe breaks section 4. Use the report action on the post or photo itself, or
              write to <a href="mailto:info@tornea.net">info@tornea.net</a> describing what and where it is.
            </p>
            <ul>
              <li>
                <strong>A report does not remove anything.</strong> It goes to the back office of the business unit
                that runs that event or community, where a person reads it and decides. The content stays up in the
                meantime, so that a single objection cannot end a conversation.
              </li>
              <li>
                <strong>A photo of you is the exception.</strong> Ask for a picture you appear in to be taken down
                and it is removed straight away, without waiting for a decision.
              </li>
              <li>
                <strong>What can follow.</strong> The content is left up or removed, and — separately — an account
                can be stopped from posting or suspended. A measure can be limited to one business unit's events and
                communities or apply across the platform, and it can be temporary or open-ended.
              </li>
              <li>
                <strong>What you are told.</strong> If your content is removed, you are notified. If your account is
                restricted, you receive an email stating the reason and how long it lasts. If you reported
                something, you are told the outcome.
              </li>
              <li>
                <strong>Review.</strong> Decisions are taken by people, not automatically. If you disagree with one,
                write to <a href="mailto:info@tornea.net">info@tornea.net</a> within a reasonable time and we will
                look at it again. This does not affect your right to go to a court or to a competent authority.
              </li>
              <li>
                <strong>Do not misuse reporting.</strong> Repeatedly filing reports that are manifestly unfounded is
                itself a breach of these Terms.
              </li>
            </ul>

            <h2>6. Events and organizers</h2>
            <p>
              Events are run by independent organizers using the Service. Tornea is not responsible for the conduct
              of organizers or participants, the awarding of prizes, or any other aspect of an event itself, beyond
              providing the platform.
            </p>
            <p>
              The organizer of an event or community moderates what is posted there: their back-office team reads
              the reports filed on it and can remove content and restrict accounts under section 5. Tornea can act
              itself where the whole platform is concerned.
            </p>

            <h2>7. Intellectual property</h2>
            <p>
              The Service, including its software, branding, and design, is owned by Tornea or its licensors and
              is protected by intellectual-property laws. These Terms do not grant you any rights to our trademarks
              or branding.
            </p>

            <h2>8. Privacy</h2>
            <p>
              Your use of the Service is also governed by our{' '}
              <Link to="/privacy">Privacy Policy</Link>, which explains what data we collect and why.
            </p>

            <h2>9. Disclaimers</h2>
            <p>
              The Service is provided "as is" and "as available", without warranties of any kind, whether express
              or implied, including warranties of merchantability, fitness for a particular purpose, or
              non-infringement. We do not guarantee that the Service will always be available, error-free, or
              secure.
            </p>

            <h2>10. Limitation of liability</h2>
            <p>
              To the maximum extent permitted by law, Tornea shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred
              directly or indirectly, arising from your use of the Service.
            </p>

            <h2>11. Termination</h2>
            <p>
              You may stop using the Service at any time. We may suspend or terminate access if you violate these
              Terms or harm other users — with the notice described in section 5 — and for other serious reasons,
              such as a legal obligation or a risk to the security of the Service. Provisions that should survive
              termination by their nature (e.g., intellectual-property, disclaimers, limitation of liability) will
              survive.
            </p>

            <h2>12. Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. The "Last updated" date at the top will reflect any
              changes. Material changes will be communicated through the app or via email where appropriate.
              Continued use of the Service after changes take effect constitutes acceptance of the updated Terms.
            </p>

            <h2>13. Governing law</h2>
            <p>
              Which law applies follows which company you contract with (section 1). Where that is Herculean CV,
              these Terms are governed by Belgian law and disputes go to the competent Belgian courts. Where that is
              Hercules Trophy Middle East FZ LLC, they are governed by the laws of the United Arab Emirates as
              applied in the Emirate of Fujairah and disputes go to the competent courts of Fujairah. In both cases
              conflict-of-laws principles are excluded.
            </p>
            <p>
              If you use the Service as a consumer, none of this deprives you of the protection of the mandatory
              rules of your own country of residence, and where that law gives you the right to bring proceedings
              before your local court, you keep it.
            </p>

            <h2>14. Contact</h2>
            <p>
              Questions about these Terms: <a href="mailto:info@tornea.net">info@tornea.net</a>, or Hercules Trophy
              Middle East FZ LLC, Creative Tower, Hamad Bin Abdullah, P.O. Box 4422, Fujairah, United Arab Emirates.
            </p>

            <Link to="/privacy" className="legal__footer-link">
              {t('privacyPolicy', { defaultValue: 'Privacy Policy' })}
              <Icon name="angle-right" />
            </Link>
          </article>
        </div>
      </div>

      <div className="bottom">
        <NavBar />
      </div>
    </Screen>
  );
};

export default Terms;
